import { FC } from 'react';
import { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';

type FlowCategory = {
  category: string;
  flowList: {
    title: string;
    description: string;
    image: { src: string; alt: string; width: string; height: string };
  }[];
};

type Props = {
  title: string;
  productName: string;
  bottomBadge: { title: string; subtitle: string };
} & { flow: FlowCategory[] };

const FlowGrid: FC<Props> = ({ title, productName, bottomBadge, flow = [] }) => {
  const flatFlowList = flow.reduce<FlowCategory['flowList']>((acum, current) => {
    acum.push(...current.flowList);
    return acum;
  }, []);

  return (
    <>
      <h4 className="mb-32 text-2xl md:text-3.5xl leading-10 font-medium lg:font-normal">
        {title}
        <b>{' ' + productName}</b>
      </h4>

      <div
        className="flex flex-col gap-8 xl:grid"
        style={{
          gridTemplateRows: `repeat(${flow.length}, minmax(32px, auto))`
        }}
      >
        <div
          className="hidden gap-8 xl:grid"
          style={{ gridTemplateColumns: `repeat(${flatFlowList.length}, minmax(0, 1fr))` }}
        >
          {flow.map(({ category, flowList }, index) => (
            <div
              key={index}
              style={{ gridColumn: `span ${flowList.length} / span ${flowList.length}` }}
              className="relative border-t border-x border-enterprise-gray-50"
            >
              <span className="absolute text-2xl -translate-x-1/2 left-1/2 -top-14">
                {category}
              </span>
            </div>
          ))}
        </div>

        <div
          className="flex flex-col xl:grid xl:gap-8 space-y-28 xl:space-y-0"
          style={{ gridTemplateColumns: `repeat(${flatFlowList.length}, minmax(0, 1fr))` }}
        >
          {Object.values(flow).map(({ category, flowList }, categoryIndex) => (
            <div
              key={categoryIndex}
              className="flex flex-col space-y-10 xl:space-y-0"
              style={{ gridColumn: `span ${flowList.length} / span ${flowList.length}` }}
            >
              <div className="relative h-8 border-t xl:hidden border-x border-enterprise-gray-50">
                <p className="absolute text-2xl -translate-x-1/2 -top-14 left-1/2 whitespace-nowrap">
                  {category}
                </p>
              </div>
              <div
                className="h-full space-y-10 lg:grid lg:gap-8 lg:space-y-0"
                style={{ gridTemplateColumns: `repeat(${flowList.length}, minmax(0, 1fr))` }}
              >
                {flowList.map(({ title, description, image }, flowIndex) => (
                  <div
                    key={flowIndex}
                    className="relative col-span-1 border rounded-lg bg-neutral-900 border-tundora"
                  >
                    <div className="relative overflow-hidden bg-black rounded-t-lg h-44">
                      <span
                        style={{ transform: 'scale(1.3)' }}
                        className="inline-block w-full h-full"
                      >
                        <Image
                          alt="flow image"
                          src={image.src}
                          layout="fill"
                          objectFit="contain"
                          quality={100}
                          draggable={false}
                          className="scale-[1.3] md:scale-[2] lg:scale-[1] pointer-events-none :pointer-events-auto xl:hover:scale-[1.1] -translate-x-14 md:-translate-x-20 md:translate-y-8 lg:-translate-y-2 lg:-translate-x-10 -translate-y-2 rotate-[25deg] lg:hover:-translate-x-6 lg:hover:-translate-y-0 transition-transform duration-500 select-none"
                        />
                      </span>
                    </div>
                    <div className="p-6 rounded-b-lg">
                      <p className="mb-4 text-2xl font-medium">{title}</p>
                      <p className="text-neutral-400">{description}</p>
                    </div>
                    {Boolean(flowIndex !== 0 || categoryIndex !== 0) && (
                      <div
                        className={cn(
                          flowIndex !== 0 && 'lg:flex',
                          'absolute -left-[10px] top-1/2 -translate-y-1/2 hidden xl:flex'
                        )}
                      >
                        <div className="relative z-10 flex items-center justify-center w-5 h-5 bg-black border rounded-full border-tundora">
                          <svg
                            width="6"
                            height="10"
                            viewBox="0 0 6 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M0.694895 9.53033C0.987307 9.82322 1.4614 9.82322 1.75381 9.53033L5.45646 5.82159C5.74888 5.5287 5.74888 5.05382 5.45646 4.76093L1.75381 1.05219C1.4614 0.759296 0.987307 0.759296 0.694895 1.05219C0.402483 1.34508 0.402483 1.81996 0.694895 2.11285L3.86809 5.29126L0.694895 8.46967C0.402483 8.76256 0.402483 9.23744 0.694895 9.53033Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                        <div
                          className="absolute w-8 h-[2px] top-1/2 -translate-y-1/2 -left-5"
                          style={{
                            backgroundImage:
                              'linear-gradient(90.13deg, rgba(52, 52, 52, 0) -3.25%, #6F6F6F 20.42%, #6F6F6F 99.89%)'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {bottomBadge && (
        <div className="flex items-center justify-center p-6 mt-16 text-center border rounded-lg bg-neutral-900 md:py-8 md:px-12 xl:mt-8 border-tundora">
          <div className="relative flex-1 hidden lg:block">
            <div className="absolute right-0 w-1/6 h-1 -translate-y-1/2 bg-gradient-to-l from-neutral-900 to-transparent top-1/2" />
            <div className="absolute left-0 flex items-center justify-center -translate-y-1/2 rounded-full w-9 h-9 bg-scale-lightmauve top-1/2">
              <svg
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M8.89301 0.931705C8.38644 0.440978 7.56511 0.440978 7.05853 0.931705L0.644008 7.1455C0.137429 7.63623 0.137429 8.43185 0.644008 8.92258L7.05853 15.1364C7.56511 15.6271 8.38644 15.6271 8.89301 15.1364C9.39959 14.6456 9.39959 13.85 8.89301 13.3593L3.39574 8.03404L8.89301 2.70878C9.39959 2.21806 9.39959 1.42243 8.89301 0.931705Z"
                  fill="white"
                />
              </svg>
            </div>
            <div className="bg-scale-lightmauve h-[2px]" />
          </div>
          <div className="space-y-4 md:space-y-2 lg:mx-20 xl:mx-24">
            <p className="text-2xl font-medium leading-8">{bottomBadge.title}</p>
            <p className="text-neutral-400">{bottomBadge.subtitle}</p>
          </div>
          <div className="relative flex-1 hidden lg:block">
            <div className="absolute w-1/6 h-1 -translate-y-1/2 bg-gradient-to-r from-neutral-900 to-transparent top-1/2" />
            <div className="absolute right-0 flex items-center justify-center -translate-y-1/2 rounded-full w-9 h-9 bg-scale-lightmauve top-1/2">
              <svg
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M0.427786 0.839542C0.934365 0.348815 1.75569 0.348815 2.26227 0.839542L8.67679 7.05334C9.18337 7.54406 9.18337 8.33969 8.67679 8.83042L2.26227 15.0442C1.75569 15.5349 0.934365 15.5349 0.427786 15.0442C-0.0787932 14.5535 -0.0787932 13.7579 0.427786 13.2671L5.92507 7.94188L0.427786 2.61662C-0.0787932 2.12589 -0.0787932 1.33027 0.427786 0.839542Z"
                  fill="white"
                />
              </svg>
            </div>
            <div className="bg-scale-lightmauve h-[2px]" />
          </div>
        </div>
      )}
    </>
  );
};

export default FlowGrid;
